import React, { useState, useEffect, useCallback } from 'react';
import { ChevronRight, ChevronDown, Folder, FolderOpen, MoreVertical, Loader, Brain } from 'lucide-react';
import { documentService } from '../../services/api';

const FolderItem = ({
  folder,
  isSelected,
  isExpanded,
  onSelect,
  onToggle,
  onEdit,
  onDelete,
  onCreateSubfolder,
  onDropDocument,
  onDocumentClick,
  level = 0
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showAllDocuments, setShowAllDocuments] = useState(false);
  const [allDocuments, setAllDocuments] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [documentsLoaded, setDocumentsLoaded] = useState(false);
  const [documentLoadError, setDocumentLoadError] = useState(null);
  const [hoveredDoc, setHoveredDoc] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });

  // Fetch all documents for this folder when expanded
  const fetchAllDocuments = useCallback(async () => {
    if (documentsLoaded || loadingDocuments || !folder.id) return;

    try {
      setLoadingDocuments(true);
      setDocumentLoadError(null);

      const response = await documentService.getDocuments({
        folder_id: folder.id,
        page_size: 100 // Get up to 100 documents
      });

      // These documents have full metadata (file_size, mime_type, etc.)
      setAllDocuments(response.data.documents || []);
      setDocumentsLoaded(true);
    } catch (err) {
      console.error('Failed to load folder documents:', err);
      setDocumentLoadError('Failed to load documents');
    } finally {
      setLoadingDocuments(false);
    }
  }, [folder.id, documentsLoaded, loadingDocuments]);

  // Load documents when user wants to see all
  useEffect(() => {
    if (showAllDocuments && !documentsLoaded && folder.document_count > 3) {
      fetchAllDocuments();
    }
  }, [showAllDocuments, documentsLoaded, folder.document_count, fetchAllDocuments]);

  const handleContextMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowMenu(!showMenu);
  };

  const handleMenuAction = (action, e) => {
    e.stopPropagation();
    setShowMenu(false);
    action();
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const documentId = e.dataTransfer.getData('application/document-id');
    if (documentId && onDropDocument) {
      onDropDocument(documentId, folder.id);
    }
  };

  const iconColor = folder.color || '#6B7280';

  return (
    <div className="relative">
      <div
        className={`
          group flex items-center gap-2 px-3 py-2 cursor-pointer rounded-md transition-colors
          ${isSelected ? 'bg-gray-50 text-yellow-600' : 'hover:bg-gray-100 text-gray-700'}
          ${isDragOver ? 'bg-gray-100 border-2 border-blue-400 border-dashed' : ''}
          ${level > 0 ? 'ml-' + (level * 4) : ''}
        `}
        onClick={() => onSelect(folder)}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {/* Expand/Collapse Icon */}
        {folder.subfolders && folder.subfolders.length > 0 ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggle(folder.id);
            }}
            aria-label={isExpanded ? 'Collapse folder' : 'Expand folder'}
            className="p-0.5 hover:bg-gray-200 rounded"
          >
            {isExpanded ? (
              <ChevronDown className="h-4 w-4 text-gray-500" />
            ) : (
              <ChevronRight className="h-4 w-4 text-gray-500" />
            )}
          </button>
        ) : (
          <div className="w-5" />
        )}

        {/* Folder Icon */}
        <div style={{ color: iconColor }}>
          {isExpanded ? (
            <FolderOpen className="h-4 w-4" />
          ) : (
            <Folder className="h-4 w-4" />
          )}
        </div>

        {/* Folder Name */}
        <span className="flex-1 text-sm font-medium truncate">
          {folder.name}
        </span>

        {/* Document Count Badge */}
        {folder.document_count > 0 && (
          <span className="text-xs px-2 py-0.5 bg-gray-200 text-gray-600 rounded-full">
            {folder.document_count}
          </span>
        )}

        {/* Context Menu Button */}
        <button
          onClick={handleContextMenu}
          aria-label="Folder options"
          className="opacity-0 group-hover:opacity-100 p-1 hover:bg-gray-200 rounded transition-opacity"
        >
          <MoreVertical className="h-4 w-4 text-gray-500" />
        </button>
      </div>

      {/* Document Previews */}
      {folder.documents && folder.documents.length > 0 && (
        <div className="ml-9 mt-1 space-y-0.5">
          {/* Display documents - either initial preview or all loaded documents */}
          {(showAllDocuments && documentsLoaded ? allDocuments : folder.documents.slice(0, 3)).map((doc) => (
            <div
              key={doc.id}
              className="relative"
            >
              <div
                className="text-xs text-gray-500 truncate pr-2 cursor-pointer hover:text-yellow-600 hover:underline flex items-center gap-1"
                onMouseEnter={(e) => {
                  if (doc.ai_summary) {
                    const rect = e.currentTarget.getBoundingClientRect();
                    setTooltipPosition({
                      top: rect.top + rect.height / 2,
                      left: rect.right + 8
                    });
                    setHoveredDoc(doc.id);
                  }
                }}
                onMouseLeave={() => setHoveredDoc(null)}
                onClick={(e) => {
                  e.stopPropagation();
                  if (onDocumentClick) {
                    onDocumentClick(doc);
                  }
                }}
              >
                • {doc.original_file_name}
                {doc.ai_summary && (
                  <Brain className="h-3 w-3 text-purple-500 flex-shrink-0" />
                )}
              </div>

              {/* AI Summary Tooltip - rendered with fixed positioning */}
              {doc.ai_summary && hoveredDoc === doc.id && (
                <div
                  className="fixed z-[9999] w-72 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-xl pointer-events-none"
                  style={{
                    top: `${tooltipPosition.top}px`,
                    left: `${tooltipPosition.left}px`,
                    transform: 'translateY(-50%)'
                  }}
                >
                  <div className="flex items-center gap-1 mb-2 text-purple-300">
                    <Brain className="h-3 w-3" />
                    <span className="font-semibold">AI Summary</span>
                  </div>
                  <p className="leading-relaxed">{doc.ai_summary}</p>
                  <div className="absolute -left-1 top-1/2 -translate-y-1/2 w-2 h-2 bg-gray-900 transform rotate-45"></div>
                </div>
              )}
            </div>
          ))}

          {/* Loading state */}
          {loadingDocuments && (
            <div className="text-xs text-gray-400 italic flex items-center gap-1">
              <Loader className="h-3 w-3 animate-spin" />
              Loading documents...
            </div>
          )}

          {/* Error state */}
          {documentLoadError && (
            <div className="text-xs text-red-500 italic">
              {documentLoadError}
            </div>
          )}

          {/* Show more button */}
          {folder.document_count > 3 && !showAllDocuments && !loadingDocuments && (
            <button
              className="text-xs text-gray-600 hover:text-yellow-600 italic cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                setShowAllDocuments(true);
              }}
            >
              +{folder.document_count - 3} more...
            </button>
          )}

          {/* Show less button */}
          {showAllDocuments && documentsLoaded && allDocuments.length > 3 && (
            <button
              className="text-xs text-gray-600 hover:text-yellow-600 italic cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                setShowAllDocuments(false);
              }}
            >
              Show less
            </button>
          )}
        </div>
      )}

      {/* Context Menu */}
      {showMenu && (
        <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-50">
          <div className="py-1">
            <button
              onClick={(e) => handleMenuAction(() => onCreateSubfolder(folder), e)}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            >
              Create Subfolder
            </button>
            <button
              onClick={(e) => handleMenuAction(() => onEdit(folder), e)}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            >
              Rename
            </button>
            <button
              onClick={(e) => handleMenuAction(() => onDelete(folder), e)}
              className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
            >
              Delete
            </button>
          </div>
        </div>
      )}

      {/* Render Subfolders */}
      {isExpanded && folder.subfolders && folder.subfolders.length > 0 && (
        <div className="ml-4">
          {folder.subfolders.map(subfolder => (
            <FolderItem
              key={subfolder.id}
              folder={subfolder}
              isSelected={isSelected}
              isExpanded={isExpanded}
              onSelect={onSelect}
              onToggle={onToggle}
              onEdit={onEdit}
              onDelete={onDelete}
              onCreateSubfolder={onCreateSubfolder}
              onDropDocument={onDropDocument}
              onDocumentClick={onDocumentClick}
              level={level + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default FolderItem;
